/**
 * The owner's reminders to settle their subscription before the shop is
 * locked. Three warnings that get more serious, then the overdue notice.
 * `count` is whole days left; `date` is the due date, already formatted.
 */
const subscription = {
  en: {
    dismiss: "Dismiss",
    howToPay: "How to pay",
    page: {
      title: "Your subscription",
      plan: "Plan",
      noPlan: "No plan set",
      price: "Price",
      perMonthly: "{amount} per month",
      perYearly: "{amount} per year",
      status: "Status",
      statuses: {
        trialing: "Free trial",
        active: "Active",
        past_due: "Payment overdue",
        suspended: "Suspended",
        cancelled: "Cancelled",
      },
      nextDue: "Next payment due",
      noDate: "No date set",
      grace: "Days of grace after the due date: {days}",
      howToPay: "How to pay",
      noInstructions: "Get in touch with us to arrange your payment.",
      methods: "We accept: {list}",
      whatsapp: "Pay or ask on WhatsApp",
      whatsappMessage: "Hello, I am {shop} ({slug}). I would like to pay my subscription.",
      history: "Payments",
      none: "No payment recorded yet.",
      period: "Covers {from} to {to}",
      receipt: "Receipt",
      invoice: "Invoice",
      openFailed: "Could not open the document.",
      popupBlocked: "Your browser blocked the new tab. Allow pop-ups for this site and try again.",
      loadFailed: "Could not load your subscription.",
      method: {
        cash: "Cash",
        d17: "D17",
        flouci: "Flouci",
        bank_transfer: "Bank transfer",
        card: "Card",
        other: "Other",
      },
    },
    week: {
      trial: {
        one: "Your free trial ends in {count} day, on {date}. Settle your subscription to keep using the shop without interruption.",
        other:
          "Your free trial ends in {count} days, on {date}. Settle your subscription to keep using the shop without interruption.",
      },
      period: {
        one: "Your subscription is due in {count} day, on {date}. Please settle your payment to keep using the shop without interruption.",
        other:
          "Your subscription is due in {count} days, on {date}. Please settle your payment to keep using the shop without interruption.",
      },
    },
    threeDays: {
      trial: {
        one: "Only {count} day left in your free trial. Settle your subscription now — the shop will be locked shortly after it ends.",
        other:
          "Only {count} days left in your free trial. Settle your subscription now — the shop will be locked shortly after it ends.",
      },
      period: {
        one: "Only {count} day left to pay your subscription. Settle it now — the shop will be locked shortly after the due date.",
        other:
          "Only {count} days left to pay your subscription. Settle it now — the shop will be locked shortly after the due date.",
      },
    },
    lastDay: {
      trial:
        "Last day of your free trial. Settle your subscription today — tomorrow the payment is overdue and, after a short grace period, the shop is locked.",
      period:
        "Today is the last day to pay your subscription. Settle it today — tomorrow the payment is overdue and, after a short grace period, the shop is locked.",
    },
    overdue: {
      locksAt:
        "Your subscription payment is overdue. The register and dashboard will be locked on {time} unless you settle it before then.",
      soon: "Your subscription payment is overdue. The register and dashboard will be locked soon unless you settle it.",
    },
  },

  fr: {
    dismiss: "Fermer",
    howToPay: "Comment payer",
    page: {
      title: "Votre abonnement",
      plan: "Formule",
      noPlan: "Aucune formule définie",
      price: "Prix",
      perMonthly: "{amount} par mois",
      perYearly: "{amount} par an",
      status: "Statut",
      statuses: {
        trialing: "Essai gratuit",
        active: "Actif",
        past_due: "Paiement en retard",
        suspended: "Suspendu",
        cancelled: "Résilié",
      },
      nextDue: "Prochain paiement dû",
      noDate: "Aucune date définie",
      grace: "Jours de grâce après l'échéance : {days}",
      howToPay: "Comment payer",
      noInstructions: "Contactez-nous pour organiser votre paiement.",
      methods: "Nous acceptons : {list}",
      whatsapp: "Payer ou demander sur WhatsApp",
      whatsappMessage: "Bonjour, je suis {shop} ({slug}). Je voudrais payer mon abonnement.",
      history: "Paiements",
      none: "Aucun paiement enregistré pour l'instant.",
      period: "Couvre du {from} au {to}",
      receipt: "Reçu",
      invoice: "Facture",
      openFailed: "Impossible d'ouvrir le document.",
      popupBlocked: "Votre navigateur a bloqué le nouvel onglet. Autorisez les pop-ups pour ce site et réessayez.",
      loadFailed: "Impossible de charger votre abonnement.",
      method: {
        cash: "Espèces",
        d17: "D17",
        flouci: "Flouci",
        bank_transfer: "Virement bancaire",
        card: "Carte",
        other: "Autre",
      },
    },
    week: {
      trial: {
        one: "Votre essai gratuit se termine dans {count} jour, le {date}. Réglez votre abonnement pour continuer à utiliser la boutique sans interruption.",
        other:
          "Votre essai gratuit se termine dans {count} jours, le {date}. Réglez votre abonnement pour continuer à utiliser la boutique sans interruption.",
      },
      period: {
        one: "Votre abonnement arrive à échéance dans {count} jour, le {date}. Merci de régler votre paiement pour continuer sans interruption.",
        other:
          "Votre abonnement arrive à échéance dans {count} jours, le {date}. Merci de régler votre paiement pour continuer sans interruption.",
      },
    },
    threeDays: {
      trial: {
        one: "Plus que {count} jour d'essai gratuit. Réglez votre abonnement maintenant — la boutique sera verrouillée peu après la fin.",
        other:
          "Plus que {count} jours d'essai gratuit. Réglez votre abonnement maintenant — la boutique sera verrouillée peu après la fin.",
      },
      period: {
        one: "Plus que {count} jour pour payer votre abonnement. Réglez-le maintenant — la boutique sera verrouillée peu après l'échéance.",
        other:
          "Plus que {count} jours pour payer votre abonnement. Réglez-le maintenant — la boutique sera verrouillée peu après l'échéance.",
      },
    },
    lastDay: {
      trial:
        "Dernier jour de votre essai gratuit. Réglez votre abonnement aujourd'hui — demain le paiement sera en retard et, après un court délai de grâce, la boutique sera verrouillée.",
      period:
        "Aujourd'hui est le dernier jour pour payer votre abonnement. Réglez-le aujourd'hui — demain le paiement sera en retard et, après un court délai de grâce, la boutique sera verrouillée.",
    },
    overdue: {
      locksAt:
        "Le paiement de votre abonnement est en retard. La caisse et le tableau de bord seront verrouillés le {time} si vous ne le réglez pas avant.",
      soon: "Le paiement de votre abonnement est en retard. La caisse et le tableau de bord seront bientôt verrouillés si vous ne le réglez pas.",
    },
  },

  ar: {
    dismiss: "إغلاق",
    howToPay: "كيفية الدفع",
    page: {
      title: "اشتراكك",
      plan: "الباقة",
      noPlan: "لم تُحدَّد باقة",
      price: "السعر",
      perMonthly: "{amount} شهرياً",
      perYearly: "{amount} سنوياً",
      status: "الحالة",
      statuses: {
        trialing: "تجربة مجانية",
        active: "نشط",
        past_due: "السداد متأخر",
        suspended: "موقوف",
        cancelled: "ملغى",
      },
      nextDue: "موعد الدفعة القادمة",
      noDate: "لا يوجد تاريخ",
      grace: "أيام السماح بعد الاستحقاق: {days}",
      howToPay: "كيفية الدفع",
      noInstructions: "تواصل معنا لترتيب الدفع.",
      methods: "نقبل: {list}",
      whatsapp: "ادفع أو اسأل عبر واتساب",
      whatsappMessage: "مرحباً، أنا {shop} ({slug}). أودّ دفع اشتراكي.",
      history: "الدفعات",
      none: "لم تُسجَّل أي دفعة بعد.",
      period: "تغطي من {from} إلى {to}",
      receipt: "وصل",
      invoice: "فاتورة",
      openFailed: "تعذّر فتح الوثيقة.",
      popupBlocked: "حظر المتصفح التبويب الجديد. اسمح بالنوافذ المنبثقة لهذا الموقع وحاول مجدداً.",
      loadFailed: "تعذّر تحميل اشتراكك.",
      method: {
        cash: "نقداً",
        d17: "D17",
        flouci: "Flouci",
        bank_transfer: "تحويل بنكي",
        card: "بطاقة",
        other: "أخرى",
      },
    },
    week: {
      trial: {
        one: "تنتهي تجربتك المجانية خلال يوم واحد، في {date}. سدّد اشتراكك لمواصلة استخدام المتجر دون انقطاع.",
        two: "تنتهي تجربتك المجانية خلال يومين، في {date}. سدّد اشتراكك لمواصلة استخدام المتجر دون انقطاع.",
        few: "تنتهي تجربتك المجانية خلال {count} أيام، في {date}. سدّد اشتراكك لمواصلة استخدام المتجر دون انقطاع.",
        other:
          "تنتهي تجربتك المجانية خلال {count} يوماً، في {date}. سدّد اشتراكك لمواصلة استخدام المتجر دون انقطاع.",
      },
      period: {
        one: "يحين موعد سداد اشتراكك خلال يوم واحد، في {date}. يرجى السداد لمواصلة استخدام المتجر دون انقطاع.",
        two: "يحين موعد سداد اشتراكك خلال يومين، في {date}. يرجى السداد لمواصلة استخدام المتجر دون انقطاع.",
        few: "يحين موعد سداد اشتراكك خلال {count} أيام، في {date}. يرجى السداد لمواصلة استخدام المتجر دون انقطاع.",
        other:
          "يحين موعد سداد اشتراكك خلال {count} يوماً، في {date}. يرجى السداد لمواصلة استخدام المتجر دون انقطاع.",
      },
    },
    threeDays: {
      trial: {
        one: "بقي يوم واحد فقط من تجربتك المجانية. سدّد اشتراكك الآن — سيُقفل المتجر بعد انتهائها بقليل.",
        two: "بقي يومان فقط من تجربتك المجانية. سدّد اشتراكك الآن — سيُقفل المتجر بعد انتهائها بقليل.",
        few: "بقيت {count} أيام فقط من تجربتك المجانية. سدّد اشتراكك الآن — سيُقفل المتجر بعد انتهائها بقليل.",
        other:
          "بقيت {count} أيام فقط من تجربتك المجانية. سدّد اشتراكك الآن — سيُقفل المتجر بعد انتهائها بقليل.",
      },
      period: {
        one: "بقي يوم واحد فقط لسداد اشتراكك. سدّده الآن — سيُقفل المتجر بعد موعد الاستحقاق بقليل.",
        two: "بقي يومان فقط لسداد اشتراكك. سدّده الآن — سيُقفل المتجر بعد موعد الاستحقاق بقليل.",
        few: "بقيت {count} أيام فقط لسداد اشتراكك. سدّده الآن — سيُقفل المتجر بعد موعد الاستحقاق بقليل.",
        other:
          "بقيت {count} أيام فقط لسداد اشتراكك. سدّده الآن — سيُقفل المتجر بعد موعد الاستحقاق بقليل.",
      },
    },
    lastDay: {
      trial:
        "اليوم آخر يوم في تجربتك المجانية. سدّد اشتراكك اليوم — غداً يصبح السداد متأخراً، وبعد مهلة سماح قصيرة يُقفل المتجر.",
      period:
        "اليوم آخر يوم لسداد اشتراكك. سدّده اليوم — غداً يصبح السداد متأخراً، وبعد مهلة سماح قصيرة يُقفل المتجر.",
    },
    overdue: {
      locksAt:
        "سداد اشتراكك متأخر. ستُقفل نقطة البيع ولوحة التحكم في {time} ما لم تسدّده قبل ذلك.",
      soon: "سداد اشتراكك متأخر. ستُقفل نقطة البيع ولوحة التحكم قريباً ما لم تسدّده.",
    },
  },
};

export default subscription;
